
import React from 'react';
import { motion } from 'motion/react';
import { SimulationConfig } from '@/types';
import { SectionLabel } from './SectionLabel';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { SETTING_INFO } from '@/data/settingInfo';

interface TrailSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  getLockInfo?: (id: string) => { isLocked: boolean; min_plan?: string };
}

export const TrailSettingsSection: React.FC<TrailSettingsSectionProps> = ({ config, setConfig, isLocked, getLockInfo }) => {
  const isTrailMode = config.mode === 'TRAIL';
  const disabledMsg = "This setting is optimized for TRAIL mode.";
  const isLockedFn = (id: string) => getLockInfo?.(id).isLocked || false;

  return (
    <div className="w-full flex flex-col gap-3 mb-4">
      <ControlGroup 
        label="Trail Dynamics" 
        settingKey="trailEnabled"
        darkMode={config.darkMode}
      >
        <Toggle active={config.trailEnabled} onClick={() => setConfig(p => {
          const next = !p.trailEnabled;
          return { ...p, trailEnabled: next, enableTrails: next };
        })} color="bg-purple-600" />
      </ControlGroup>

      {config.trailEnabled && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="flex flex-col gap-3 overflow-hidden"
        >
          <ControlGroup 
            label="Infinite Trail" 
            settingKey="trailPersistence"
            darkMode={config.darkMode}
          >
            <Toggle active={config.trailPersistence} onClick={() => setConfig(p => ({...p, trailPersistence: !p.trailPersistence}))} color="bg-purple-600" />
          </ControlGroup>

          <ControlGroup 
            label="Performance Mode (Occlusion)" 
            settingKey="trailOcclusionOptimization"
            darkMode={config.darkMode}
          >
            <Toggle active={config.trailOcclusionOptimization} onClick={() => setConfig(p => ({...p, trailOcclusionOptimization: !p.trailOcclusionOptimization}))} color="bg-purple-600" />
          </ControlGroup>

          {/* NEW: BORDER CONTROLS */}
          <div className="p-2 rounded-xl border border-black/5 bg-black/5 flex flex-col gap-1">
              <span className="text-[9px] font-black uppercase opacity-60">Trail Outer Border</span>
              <div className="grid grid-cols-1 gap-1">
                  <SmartInput label="Width" min={0} max={10} value={config.trailStrokeWidth} onChange={v => setConfig(p => ({...p, trailStrokeWidth: v}))} darkMode={config.darkMode} settingKey="trailStrokeWidth" />
                  <ControlGroup label="Border Color" settingKey="trailStrokeColor" darkMode={config.darkMode}>
                    <input 
                      type="color" 
                      value={config.trailStrokeColor} 
                      onChange={e => setConfig(p => ({...p, trailStrokeColor: e.target.value}))} 
                      className="w-full h-8 rounded-lg cursor-pointer bg-transparent" 
                    />
                  </ControlGroup>
              </div>
              <ControlGroup label="Apply to Persistence" settingKey="trailApplyBorderToPersistence" darkMode={config.darkMode}>
                <Toggle active={config.trailApplyBorderToPersistence} onClick={() => setConfig(p => ({...p, trailApplyBorderToPersistence: !p.trailApplyBorderToPersistence}))} color="bg-purple-600" />
              </ControlGroup>
          </div>

          <div className="grid grid-cols-1 gap-1 mb-2 mt-2">
            <ControlGroup label="Rainbow Fill" settingKey="useTrailRGB" darkMode={config.darkMode}>
              <Toggle active={config.useTrailRGB} onClick={() => setConfig(p => ({...p, useTrailRGB: !p.useTrailRGB}))} color="bg-purple-600" />
            </ControlGroup>
            <ControlGroup label="Custom Fill" settingKey="trailCustomColor" darkMode={config.darkMode}>
              <input 
                type="color" 
                value={config.trailCustomColor} 
                onChange={e => setConfig(p => ({...p, trailCustomColor: e.target.value}))} 
                disabled={config.useTrailRGB}
                className="w-full h-8 rounded-lg cursor-pointer bg-transparent disabled:opacity-30" 
              />
            </ControlGroup>
          </div>

          <div className="flex flex-col gap-3">
              <SmartInput label="Length" min={0} max={200} value={config.trailLength} onChange={v => setConfig(p => ({...p, trailLength: v}))} darkMode={config.darkMode} settingKey="trailLength" />
              <div className="grid grid-cols-2 gap-3">
                  <SmartInput label="Fill Opacity" min={0} max={1} step={0.01} value={config.trailOpacity} onChange={v => setConfig(p => ({...p, trailOpacity: v}))} darkMode={config.darkMode} settingKey="trailOpacity" />
                  <SmartInput label="Stroke Opacity" min={0} max={1} step={0.01} value={config.trailStrokeOpacity} onChange={v => setConfig(p => ({...p, trailStrokeOpacity: v}))} darkMode={config.darkMode} settingKey="trailStrokeOpacity" />
              </div>
          </div>

          <div className="flex flex-col gap-3">
              <SmartInput label="Thickness" min={0.1} max={10} step={0.1} value={config.trailWidthMultiplier} onChange={v => setConfig(p => ({...p, trailWidthMultiplier: v}))} darkMode={config.darkMode} settingKey="trailWidthMultiplier" />
              <SmartInput label="Glow" min={0} max={50} value={config.trailGlowAmount} onChange={v => setConfig(p => ({...p, trailGlowAmount: v}))} darkMode={config.darkMode} settingKey="trailGlowAmount" />
          </div>

          <div className="flex flex-col gap-3">
              <SmartInput label="Start Scale" min={0.1} max={3} step={0.1} value={config.trailStartScale} onChange={v => setConfig(p => ({...p, trailStartScale: v}))} darkMode={config.darkMode} settingKey="trailStartScale" disabled={config.trailMatchBallSize} />
              <SmartInput label="End Scale" min={0.1} max={3} step={0.1} value={config.trailEndScale} onChange={v => setConfig(p => ({...p, trailEndScale: v}))} darkMode={config.darkMode} settingKey="trailEndScale" disabled={config.trailMatchBallSize} />
              <ControlGroup label="Match Ball Size" settingKey="trailMatchBallSize" darkMode={config.darkMode}>
                <Toggle active={config.trailMatchBallSize} onClick={() => setConfig(p => ({...p, trailMatchBallSize: !p.trailMatchBallSize}))} color="bg-purple-600" />
              </ControlGroup>
          </div>

          <SmartInput 
            label="Spawn Interval" 
            min={1} 
            max={500} 
            value={config.trailInterval} 
            onChange={v => setConfig(p => ({...p, trailInterval: v}))} 
            darkMode={config.darkMode} 
            settingKey="trailInterval" 
            disabled={config.trailDistanceBased}
            disabledMessage="Inactive when Distance Based Spawning is ON. Use Distance Interval below."
          />

          <div className="p-2 rounded-xl border border-purple-500/10 bg-purple-500/5 flex flex-col gap-1">
              <ControlGroup label="Distance Based Spawning" settingKey="trailDistanceBased" darkMode={config.darkMode}>
                <Toggle active={config.trailDistanceBased} onClick={() => setConfig(p => ({...p, trailDistanceBased: !p.trailDistanceBased}))} color="bg-purple-600" />
              </ControlGroup>
              {config.trailDistanceBased && (
                  <SmartInput label="Distance Interval" min={1} max={100} value={config.trailDistanceInterval} onChange={v => setConfig(p => ({...p, trailDistanceInterval: v}))} darkMode={config.darkMode} settingKey="trailDistanceInterval" />
              )}
          </div>

          <div className="w-full h-[1px] bg-white/10 my-2"></div>
          <span className="text-[10px] font-black uppercase text-purple-400 opacity-80 mb-1">Start Parameters</span>
          
          <div className="flex flex-col gap-1">
              <SmartInput label="Start X Offset" min={-500} max={500} value={config.trailStartPosX} onChange={v => setConfig(p => ({...p, trailStartPosX: v}))} darkMode={config.darkMode} settingKey="trailStartPosX" />
              <SmartInput label="Start Y Offset" min={-500} max={500} value={config.trailStartPosY} onChange={v => setConfig(p => ({...p, trailStartPosY: v}))} darkMode={config.darkMode} settingKey="trailStartPosY" />
          </div>
          
          <div className="flex flex-col gap-1">
              <SmartInput label="Start Angle" min={0} max={360} value={config.trailStartAngle} onChange={v => setConfig(p => ({...p, trailStartAngle: v}))} darkMode={config.darkMode} settingKey="trailStartAngle" />
              <SmartInput label="Start Speed" min={0} max={100} value={config.trailStartSpeed} onChange={v => setConfig(p => ({...p, trailStartSpeed: v}))} darkMode={config.darkMode} settingKey="trailStartSpeed" />
          </div>

          <div className="w-full h-[1px] bg-white/10 my-2"></div>
          <ControlGroup label="Border Break Effect" settingKey="trailEnableBorderBreak" darkMode={config.darkMode}>
            <Toggle active={config.trailEnableBorderBreak} onClick={() => setConfig(p => ({...p, trailEnableBorderBreak: !p.trailEnableBorderBreak}))} color="bg-purple-600" />
          </ControlGroup>
        </motion.div>
      )}
    </div>
  );
};
